import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorNordicGrey5, Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import type { FooterBottomViewModel } from 'autogen/swagger/all';
import { EMAAltText } from 'autogen/translation-keys/trans-website-imagealttext';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';
import { breakpoint, colors, sizes } from 'variables';
import ContactInfo from './ContactInfo';
import SupportHours from './SupportHours';

const Wrapper = styled.div`
    display: block;
    background-color: ${({ theme }) => theme.ColorBackgroundSecondary};
    color: ${({ theme }) => theme.ColorTextPrimary};
    padding: 25px 0 50px;

    ${breakpoint.up(sizes.md)} {
        padding: 40px 0 50px;
    }
`;

const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
        'icon-group'
        'contact'
        'hours';
    gap: ${Spacing16};
    padding: 0 ${Spacing16};

    ${breakpoint.up(sizes.sm)} {
        grid-template-columns: repeat(2, 1fr);
        grid-template-areas:
        'icon-group icon-group'
        'contact hours';
    }

    ${breakpoint.up(sizes.lg)} {
        grid-template-columns: repeat(4, 1fr);
        grid-template-areas: 'contact hours icon-group .';
    }

    ${breakpoint.up(sizes.xl)} {
        column-gap: 40px;
    }

    ${breakpoint.up(sizes.xxl)} {
        column-gap: 90px;
    }

    ${breakpoint.up(sizes.xxxl)} {
        column-gap: 180px;
    }
`;

const ContactGroup = styled.div`
    display: flex;
    align-items: center;
    grid-area: contact;
`;

const HoursGroup = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    grid-area: hours;

    ${breakpoint.up(sizes.sm)} {
        justify-content: flex-end;
    }

    ${breakpoint.up(sizes.lg)} {
        justify-content: start;
    }
`;

const IconGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    grid-area: icon-group;
`;

const EmaLogo = styled(LinkAction)`
    margin-right: ${Spacing8};
`;

export type BottomFooterType = {
    model: FooterBottomViewModel;
};

export default function BottomFooter(props: BottomFooterType): JSX.Element {
    const {
        model: { contact, openingHours, emaLogo, smileyLogo }
    } = props;
    const { t } = useTranslation();

    return (
        <Wrapper>
            <Container className="container-fluid">
                <ContactGroup>
                    <ContactInfo contact={contact} />
                </ContactGroup>
                <HoursGroup>
                    <SupportHours hours={openingHours} />
                </HoursGroup>
                <IconGroup>
                    {emaLogo && (
                        <EmaLogo action={emaLogo.informationUrl} target="_blank" rel="noreferrer">
                            <img src={emaLogo.imageUrl} className="img" loading="lazy" alt={t(EMAAltText)} />
                        </EmaLogo>
                    )}
                    {smileyLogo && (
                        <LinkAction action={smileyLogo.informationLinkAction} target="_blank" rel="noreferrer">
                            <img src={smileyLogo.imageUrl} className="img" loading="lazy" alt={smileyLogo.text} />
                        </LinkAction>
                    )}
                </IconGroup>
            </Container>
        </Wrapper>
    );
}
